const BASE_URL =
  'https://yk8shvushproxy.azurewebsites.net/api/DataverseProxyAPI?code=Y0nxzxtkuWkHEDI02JdaVJ6KRPoJswUq9ChjmoYaoj07SgMmijcGJw==';
const LOCAL_SCHEDULE_SELECT_URL = '/api/local-schedule-select';

const DEFAULT_TIMEOUT_MS = 30000;

const ODATA_HEADERS = {
  'OData-MaxVersion': '4.0',
  'OData-Version': '4.0'
};

type RequestConfig = {
  action: 'select' | 'insert' | 'update' | 'delete' | 'upload' | 'download';
  query?: string;
  method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  body?: BodyInit;
  timeoutMs?: number;
  signal?: AbortSignal;
  acceptedStatuses: number[];
};

type RequestOptions = {
  timeoutMs?: number;
  signal?: AbortSignal;
};

function buildUrl(action: RequestConfig['action'], query = '') {
  const isLocalScheduleRead =
    process.env.NODE_ENV === 'development' &&
    action === 'select' &&
    /^\/crfd1_(?:tfilas|shiurs)\?/.test(query);

  if (isLocalScheduleRead) {
    return `${LOCAL_SCHEDULE_SELECT_URL}?q=${encodeURIComponent(query)}`;
  }

  return `${BASE_URL}&action=${action}&q=${query}`;
}

async function readError(response: Response) {
  try {
    const payload = await response.json();
    return payload?.error?.message || JSON.stringify(payload);
  } catch {
    return response.statusText || 'Unknown error';
  }
}

async function request<T = Response>({
  action,
  query = '',
  method = 'GET',
  headers,
  body,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  signal,
  acceptedStatuses
}: RequestConfig): Promise<T> {
  const controller = new AbortController();
  let didTimeout = false;
  const abortFromCaller = () => controller.abort();

  if (signal?.aborted) {
    controller.abort();
  } else {
    signal?.addEventListener('abort', abortFromCaller, { once: true });
  }

  const timeoutId = setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(buildUrl(action, query), {
      method,
      headers,
      body,
      signal: controller.signal
    });

    if (!acceptedStatuses.includes(response.status)) {
      throw new Error(`${action.toUpperCase()} failed - ${await readError(response)}`);
    }

    return response as unknown as T;
  } catch (error) {
    if (didTimeout) {
      throw new Error('Request timed out');
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}

async function toJson(response: Response) {
  const raw = await response.text();

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default class APIClient {
  static async select(
    query = '',
    resultType: 'json' | 'list' | 'single' = 'json',
    options: RequestOptions = {}
  ) {
    const response = await request<Response>({
      action: 'select',
      query,
      method: 'GET',
      headers: {
        ...ODATA_HEADERS,
        Prefer: 'odata.include-annotations="OData.Community.Display.V1.FormattedValue"',
        'Content-Type': 'application/json; charset=utf-8'
      },
      ...options,
      acceptedStatuses: [200]
    });

    const result = await toJson(response);

    switch (resultType) {
      case 'list':
        return result?.value || [];
      case 'single':
        return result?.value?.[0] || null;
      case 'json':
      default:
        return result;
    }
  }

  static async insert(query: string, body: object) {
    const response = await request<Response>({
      action: 'insert',
      query,
      method: 'POST',
      headers: {
        ...ODATA_HEADERS,
        Prefer: 'return=representation',
        'Content-Type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify(body),
      acceptedStatuses: [201, 204]
    });

    return toJson(response);
  }

  static async update(query: string, body: object) {
    return request<Response>({
      action: 'update',
      query,
      method: 'POST',
      headers: {
        ...ODATA_HEADERS,
        'Content-Type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify(body),
      acceptedStatuses: [200, 204]
    });
  }

  static async delete(query: string) {
    return request<Response>({
      action: 'delete',
      query,
      method: 'POST',
      headers: {
        ...ODATA_HEADERS,
        'Content-Type': 'application/json; charset=utf-8'
      },
      acceptedStatuses: [204]
    });
  }

  static async upload(query: string, file: File) {
    return request<Response>({
      action: 'upload',
      query,
      method: 'POST',
      headers: {
        ...ODATA_HEADERS,
        'Content-Type': 'application/octet-stream',
        'x-ms-file-name': encodeURIComponent(file.name)
      },
      body: file,
      acceptedStatuses: [200, 201, 204]
    });
  }

  static async download(query: string) {
    return request<Response>({
      action: 'download',
      query,
      method: 'GET',
      headers: {
        ...ODATA_HEADERS,
        'Content-Type': 'application/octet-stream'
      },
      acceptedStatuses: [200, 204]
    });
  }
}
